import {useDispatch, useSelector} from 'react-redux';
import {showToast, showUnitUpgradeModal} from '../../../../redux/actions';
import {getAvailableUnitUpgrades} from "../../../../utils/unit/getAvailableUnitUpgrades";
import {
  selectBuildingsDamage,
  selectCurrentPlayer,
  selectHexes,
  selectPlayers,
  selectSpecialBuildings,
  selectUnits
} from "../../../../redux/reducers/sharedStateReducer/selectors";
import {TOKENS} from "../../../../constants/tokens";
import {checkIfHexBlockedByEnemyUnit} from "../../../../utils/gameState/checkIfHexBlockedByEnemyUnit";
import {getSizeRecruitmentPrice} from "../../../../utils/recruitment/getSizeRecruitmentPrice";
import {getPlayerById} from "../../../../utils/players/getPlayerById";
import {getNeutralPlayerMock} from "../../../../utils/players/getNeutralPlayerMock";
import {getUnitIconDisplayProps} from "../../../../utils/factions/succubes/getUnitIconDisplayProps";
import {getUnitShootingRange} from "../../../../utils/unit/getUnitShootingRange";

export const ADDITIONAL_SUPPLY_TOOLTIP = 'Дополнительное снабжение.\n'
  + 'В фазе Войны позволяет:\n'
  + '- перебросить кости урона в ближнем бою или при стрельбе;\n'
  + '- при перемещении пройти на 1 ячейку дальше;\n'
  + '- после ближнего боя или стрельбы совершить маневр: переместиться на 1 ячейку.';

/**
 * Данные для показа статов отряда в карточке (RecruitUnitCard): панель найма и панель
 * осмотра ячейки
 */
export const useUnitStatsData = (unit) => {
  const dispatch = useDispatch();
  const hexes = useSelector(selectHexes);
  const units = useSelector(selectUnits);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const players = useSelector(selectPlayers);
  const buildingsDamage = useSelector(selectBuildingsDamage);
  const specialBuildings = useSelector(selectSpecialBuildings);

  const {id, ownerId, hexKey, type, factionId, isCurrentPlayerOwner} = unit;

  // Владелец нейтрального отряда не входит в players — используем мок, чтобы
  // отобразить нейтральную иконку (см. UnitIcon и NEUTRAL_PLAYER_COLOR)
  const owner = (ownerId ? getPlayerById(players, ownerId) : currentPlayer) || getNeutralPlayerMock();

  // Скин/цвет иконки - по исходной принадлежности отряда (см.
  // getUnitIconDisplayProps): соблазнённый Суккубом отряд должен выглядеть в
  // футере так же, как на игровом поле, а не под скином текущего владельца
  const unitIconProps = getUnitIconDisplayProps(unit, owner, players);

  const isExistingUnitSelected = !!id;

  const handleUnitUpgrade = () => {
    const token = hexes[hexKey];
    if (token !== TOKENS.BARRACKS) {
      dispatch(showToast('Отряд можно улучшать только на своей казарме!', 'warning', 3000));
      return;
    }
    if (checkIfHexBlockedByEnemyUnit(hexKey, TOKENS.BARRACKS, currentPlayer.id, units, hexes, specialBuildings, buildingsDamage)) {
      dispatch(showToast('Нельзя улучшить отряд - казарма заблокирована противником или повреждена!', 'warning', 3000));
      return;
    }

    dispatch(showUnitUpgradeModal(id));
  };
  // Улучшение доступно, только если с учетом фракции игрока (unitTypes в
  // конфиге, например у гномов без кавалерии) остался хотя бы один вариант
  const isUnitUpgradeAvailable = !!(isExistingUnitSelected && isCurrentPlayerOwner
    && getAvailableUnitUpgrades(type, factionId).length > 0);

  const {sizePrice: finalSizePrice, isDiscount: isSizeDiscount} = getSizeRecruitmentPrice(unit, currentPlayer);

  // Дальность стрельбы — только у стреляющих (катапульта, отряды эльфов).
  // Фракция — самого отряда, у еще не нанятого (в тулбаре найма) ее нет, и
  // берется фракция игрока
  const shootingRange = type ? getUnitShootingRange(type, factionId ?? owner.factionId) : null;

  return {
    unitIconProps,
    isExistingUnitSelected,
    handleUnitUpgrade,
    isUnitUpgradeAvailable,
    finalSizePrice,
    isSizeDiscount,
    shootingRange,
  };
};
